import type { ReactNode } from "react";
import classNames from "classnames";
import type { WorkDateField, WorkRoleField } from "@api/workItem.api";
import { Button } from "@design-system/Button/Button";
import { EmptyValue } from "@design-system/EmptyValue/EmptyValue";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { ClientsWorkOverviewWorkRow } from "../clientsWorkOverviewViewConfig";
import { formatWorkDate } from "../cells/WorkCloseDateCell";
import { WorkDateControl } from "../editing/WorkDateControl";
import {
    WorkRoleNames,
    WorkRolePicker,
    getWorkRoleHolderId,
} from "../editing/WorkRolePicker";
import { WORK_ROLES, useWorkEditability } from "../editing/workItemEditing";
import styles from "./WorkEditableFields.module.scss";

interface WorkPaneFieldProps<TField> {
    row: ClientsWorkOverviewWorkRow;
    field: TField;
}

const WorkPaneDate = ({
    row,
    field,
}: Readonly<WorkPaneFieldProps<WorkDateField>>) => {
    const { isItemEditable } = useWorkEditability();
    const date = row[field];
    if (!isItemEditable(row)) {
        return date ? formatWorkDate(date) : <EmptyValue />;
    }
    return (
        <WorkDateControl
            row={row}
            field={field}
            trigger={
                <Button
                    variant="primary"
                    size="md"
                    className={classNames(styles.trigger, {
                        [styles.empty]: !date,
                    })}
                >
                    {date ? formatWorkDate(date) : "Add date"}
                </Button>
            }
        />
    );
};

const WorkPaneRole = ({
    row,
    field,
    label,
}: Readonly<WorkPaneFieldProps<WorkRoleField> & { label: string }>) => {
    const { isRoleEditable } = useWorkEditability();
    return isRoleEditable(row, field) ? (
        <WorkRolePicker
            row={row}
            field={field}
            trigger={
                <Picker.Button
                    variant="primary"
                    size="md"
                    aria-label={label}
                    className={classNames(styles.trigger, {
                        [styles.empty]:
                            getWorkRoleHolderId(row, field) === null,
                    })}
                />
            }
        />
    ) : (
        <WorkRoleNames members={row[field]} />
    );
};

/**
 * The pane's engagement team and work dates: controls where the user can
 * edit them (open work, a role only while it has open tasks), names and
 * days otherwise.
 */
export const getWorkTeamAndDateFields = (
    row: ClientsWorkOverviewWorkRow,
): Array<{ label: string; value: ReactNode }> => [
    ...WORK_ROLES.map(({ field, label }) => ({
        label,
        value: <WorkPaneRole row={row} field={field} label={label} />,
    })),
    {
        label: "Work start",
        value: <WorkPaneDate row={row} field="workStartDate" />,
    },
    {
        label: "Work end",
        value: <WorkPaneDate row={row} field="workDeliveryDate" />,
    },
];
